import React, { useState } from 'react';
import { Activity, AlertTriangle, Plus, Search, ChevronDown } from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
import { useStorageState } from '../../../../hooks/useStorageState';
import DivingOperationsForm from './DivingOperationsForm';

/**
 * Diving Operations Tab - Track diving operations and safety records
 */
const DivingOperationsTab = ({ currentVessel, onAddEntry }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDive, setExpandedDive] = useState(null);
  
  // Storage for diving operations
  const storageKey = currentVessel?.id ? `diving_operations_${currentVessel.id}` : 'diving_operations';
  const { value: dives, setValue: saveDives } = useStorageState(storageKey, []);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: new Date().toTimeString().slice(0, 5),
    endTime: '',
    location: '',
    position: { latitude: '', longitude: '' },
    depth: '',
    diveType: 'commercial', // commercial, survey, inspection, salvage
    diversDeployed: 1,
    surfaceInterval: '',
    decompression: false,
    breathingMixture: 'air', // air, nitrox, trimix
    equipment: {
      scuba: false,
      surfaceSupplied: false,
      saturation: false,
      bellDiving: false
    },
    waterTemp: '',
    visibility: '',
    current: '',
    seaState: '',
    standbyDiver: '',
    diveSupervisor: '',
    notes: ''
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newDive = {
      id: Date.now().toString(),
      ...formData,
      timestamp: new Date(`${formData.date}T${formData.startTime}`).toISOString(),
      vesselName: currentVessel?.name,
      duration: calculateDuration(formData.startTime, formData.endTime)
    };
    
    const updatedDives = [newDive, ...dives].sort((a, b) => 
      new Date(b.timestamp) - new Date(a.timestamp)
    );
    
    await saveDives(updatedDives);
    
    // Also add to logbook
    onAddEntry({
      type: 'DIVING_OPS',
      category: 'operations',
      summary: `Diving operation: ${formData.diveType} - ${formData.depth}m`,
      description: `${formData.diversDeployed} diver(s) deployed at ${formData.location}`,
      details: newDive
    });
    
    setShowAddForm(false);
    resetForm();
  };
  
  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      startTime: new Date().toTimeString().slice(0, 5),
      endTime: '',
      location: '',
      position: { latitude: '', longitude: '' },
      depth: '',
      diveType: 'commercial',
      diversDeployed: 1,
      surfaceInterval: '',
      decompression: false,
      breathingMixture: 'air',
      equipment: {
        scuba: false,
        surfaceSupplied: false,
        saturation: false,
        bellDiving: false
      },
      waterTemp: '',
      visibility: '',
      current: '',
      seaState: '',
      standbyDiver: '',
      diveSupervisor: '',
      notes: ''
    });
  };
  
  const calculateDuration = (start, end) => {
    if (!start || !end) return '';
    const startMin = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1]);
    const endMin = parseInt(end.split(':')[0]) * 60 + parseInt(end.split(':')[1]);
    const duration = endMin - startMin;
    if (duration < 0) return '';
    return `${Math.floor(duration / 60)}h ${duration % 60}m`;
  };
  
  const filteredDives = dives.filter(dive => 
    searchQuery === '' || 
    JSON.stringify(dive).toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const stats = {
    totalDives: dives.length,
    thisMonth: dives.filter(d => {
      const date = new Date(d.timestamp);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length,
    totalHours: dives.reduce((acc, dive) => {
      const match = dive.duration?.match(/(\d+)h/);
      return acc + (match ? parseInt(match[1]) : 0);
    }, 0),
    lastDive: dives[0]?.timestamp ? new Date(dives[0].timestamp).toLocaleDateString() : 'N/A'
  };
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Activity className="h-6 w-6 text-cyan-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Diving Operations Log</h2>
                <p className="text-sm text-gray-600">Record commercial diving activities and safety compliance</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Log Dive
            </button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="px-6 py-4 grid grid-cols-4 gap-6">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Dives</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.totalDives}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">This Month</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.thisMonth}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Hours</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.totalHours}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Last Dive</p>
            <p className="text-sm font-medium text-gray-900 mt-1">{stats.lastDive}</p>
          </div>
        </div>
      </div>
      
      {/* Safety Requirements */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Diving Safety Requirements:</p>
            <ul className="space-y-0.5 text-xs">
              <li>• Maintain dive plan and risk assessment for each operation</li>
              <li>• Record decompression schedules and surface intervals</li>
              <li>• Document standby diver and supervisor details</li>
              <li>• Monitor environmental conditions and visibility</li>
              <li>• Keep records per IMCA D 014 guidelines</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Add Form */}
      {showAddForm && (
        <DivingOperationsForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={() => setShowAddForm(false)}
        />
      )}
      
      {/* Dive History */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900">Dive History</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search dives..."
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
        
        <div className="divide-y divide-gray-200">
          {filteredDives.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500">
              No diving operations recorded
            </div>
          ) : (
            filteredDives.map(dive => (
              <div key={dive.id} className="px-6 py-4">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedDive(expandedDive === dive.id ? null : dive.id)}
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(dive.timestamp).toLocaleDateString()} {dive.startTime}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {dive.location} • {dive.depth}m • {dive.duration}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge color={dive.decompression ? 'yellow' : 'green'} size="sm">
                        {dive.diveType}
                      </Badge>
                      <Badge color="blue" size="sm">
                        {dive.diversDeployed} diver{dive.diversDeployed > 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </div>
                  <ChevronDown 
                    className={`h-4 w-4 text-gray-400 transition-transform ${
                      expandedDive === dive.id ? 'rotate-180' : ''
                    }`}
                  />
                </div>
                
                {expandedDive === dive.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600 grid grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium">Supervisor:</span> {dive.diveSupervisor}
                    </div>
                    <div>
                      <span className="font-medium">Standby:</span> {dive.standbyDiver}
                    </div>
                    <div>
                      <span className="font-medium">Visibility:</span> {dive.visibility}m
                    </div>
                    <div>
                      <span className="font-medium">Current:</span> {dive.current} kts
                    </div>
                    {dive.notes && (
                      <div className="col-span-2">
                        <span className="font-medium">Notes:</span> {dive.notes}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DivingOperationsTab;